import {IconDeviceGamepad2, IconEye, IconUser, IconUsers} from "@tabler/icons-react";
import {memo, useCallback, useEffect, useRef} from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import styles from "./SocialEntrySelector.module.css";

/**
 * @typedef {"ayg" | "provider" | "user" | "community"} SocialEntityType
 *
 * @typedef {Object} SocialEntrySelectorProps
 * @property {boolean} isOpen
 * @property {SocialEntityType} entityType
 * @property {string} displayName
 * @property {string} [avatar] - URL or import reference
 * @property {string} [initials] - Fallback text for user avatars
 * @property {boolean} [hasStories] - Whether entity has viewable stories
 * @property {() => void} [onViewStories]
 * @property {() => void} [onGoToProfile]
 * @property {() => void} [onGoToCommunity]
 * @property {() => void} [onPlayInFeed]
 * @property {() => void} onClose
 * @property {React.RefObject<HTMLElement>} [anchorRef] - Desktop popover anchor
 */

/** Entity label i18n keys */
const ENTITY_LABEL_KEYS = {
  ayg: "socialSelectorLabelAyg",
  provider: "socialSelectorLabelProvider",
  user: "socialSelectorLabelUser",
  community: "socialSelectorLabelCommunity",
};

/**
 * Reusable social entry selector.
 *
 * Mobile: compact bottom action sheet.
 * Desktop: anchored popover next to the trigger.
 *
 * @param {SocialEntrySelectorProps} props
 */
const SocialEntrySelector = ({
  isOpen,
  entityType,
  displayName,
  avatar,
  initials = "",
  hasStories = true,
  onViewStories,
  onGoToProfile,
  onGoToCommunity,
  onPlayInFeed,
  onClose,
  anchorRef,
}) => {
  const {t} = useTranslation();
  const sheetRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = e => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Focus trap: focus the sheet on open
  useEffect(() => {
    if (!isOpen || !sheetRef.current) return;
    const firstAction = sheetRef.current.querySelector("button");
    firstAction?.focus();
  }, [isOpen]);

  // Position popover on desktop
  useEffect(() => {
    if (!isOpen || !sheetRef.current) return;
    const isDesktop = window.matchMedia("(min-width: 768px)").matches;
    if (!isDesktop) return;

    const sheet = sheetRef.current;
    const anchor = anchorRef?.current?.getBoundingClientRect();

    sheet.style.transform = "none";

    if (anchor) {
      // Position below the anchor element
      sheet.style.left = `${anchor.left + anchor.width / 2 - 140}px`;
      sheet.style.top = `${anchor.bottom + 8}px`;

      // Keep within viewport horizontally
      const rect = sheet.getBoundingClientRect();
      if (rect.right > window.innerWidth - 8) {
        sheet.style.left = `${window.innerWidth - rect.width - 8}px`;
      }
      if (rect.left < 8) {
        sheet.style.left = "8px";
      }

      // Keep within viewport vertically
      const sheetRect = sheet.getBoundingClientRect();
      if (sheetRect.bottom > window.innerHeight - 8) {
        sheet.style.top = `${anchor.top - sheetRect.height - 8}px`;
      }
      if (sheetRect.top < 8) {
        sheet.style.top = "8px";
      }
    } else {
      // Fallback: center on screen
      sheet.style.left = "50%";
      sheet.style.top = "50%";
      sheet.style.transform = "translate(-50%, -50%)";
    }
  }, [isOpen, anchorRef]);

  const handleAction = useCallback(
    action => {
      onClose();
      action?.();
    },
    [onClose]
  );

  if (!isOpen) return null;

  const ringClass = `${styles.avatarRing} ${styles[`avatarRing--${entityType}`] || ""}`;
  const label = t(ENTITY_LABEL_KEYS[entityType] || "");

  const renderAvatar = () => {
    if (entityType === "community") {
      return (
        <span className={styles.avatarIcon}>
          <IconUsers size={22} />
        </span>
      );
    }

    if (avatar) {
      return (
        <img
          className={styles.avatarImg}
          src={avatar}
          alt={displayName}
          loading="eager"
        />
      );
    }

    if (initials) {
      return <span className={styles.avatarInitials}>{initials}</span>;
    }

    return (
      <span className={styles.avatarIcon}>
        <IconUser size={22} />
      </span>
    );
  };

  /** Build action list based on entity type */
  const actions = [];

  // Stories action
  if (hasStories && onViewStories) {
    actions.push({
      key: "stories",
      icon: <IconEye size={18} />,
      iconVariant: "stories",
      label: t("socialSelectorViewStories"),
      onClick: () => handleAction(onViewStories),
    });
  }

  // Profile action
  if (entityType !== "community" && onGoToProfile) {
    actions.push({
      key: "profile",
      icon: <IconUser size={18} />,
      iconVariant: "profile",
      label: t("socialSelectorGoToProfile"),
      onClick: () => handleAction(onGoToProfile),
    });
  }

  // Community action
  if (entityType === "community" && onGoToCommunity) {
    actions.push({
      key: "community",
      icon: <IconUsers size={18} />,
      iconVariant: "community",
      label: t("socialSelectorGoToCommunity"),
      onClick: () => handleAction(onGoToCommunity),
    });
  }

  // Play in feed action (providers only)
  if (entityType === "provider" && onPlayInFeed) {
    actions.push({
      key: "feed",
      icon: <IconDeviceGamepad2 size={18} />,
      iconVariant: "feed",
      label: t("socialSelectorPlayInFeed"),
      onClick: () => handleAction(onPlayInFeed),
    });
  }

  // If only one action, execute it directly (skip selector)
  // This is handled by the parent via shouldShowSelector

  return createPortal(
    <>
      <div
        className={styles.backdrop}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={sheetRef}
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-label={t("socialSelectorOptionsFor", {name: displayName})}
      >
        {/* Handle bar (mobile only) */}
        <div className={styles.handle}>
          <div className={styles.handleBar} />
        </div>

        {/* Header */}
        <div className={styles.header}>
          <div className={ringClass}>
            <div className={styles.avatarInner}>{renderAvatar()}</div>
          </div>
          <div className={styles.headerInfo}>
            <span className={styles.headerName}>{displayName}</span>
            {label && <span className={styles.headerLabel}>{label}</span>}
          </div>
        </div>

        {/* Actions */}
        <div
          className={styles.actions}
          role="menu"
        >
          {actions.map(action => (
            <button
              key={action.key}
              type="button"
              className={styles.action}
              onClick={action.onClick}
              role="menuitem"
            >
              <span className={`${styles.actionIcon} ${styles[`actionIcon--${action.iconVariant}`]}`}>
                {action.icon}
              </span>
              <span className={styles.actionText}>
                <span className={styles.actionLabel}>{action.label}</span>
              </span>
            </button>
          ))}
        </div>

        {/* Cancel (mobile visible) */}
        <button
          type="button"
          className={styles.cancel}
          onClick={onClose}
        >
          {t("socialSelectorCancel")}
        </button>
      </div>
    </>,
    document.body
  );
};

export default memo(SocialEntrySelector);
